import React, { useCallback, useRef, useState } from "react";
import {
  Stage,
  Layer,
  Rect as KonvaRect,
  Image as KonvaImage,
  Transformer,
} from "react-konva";
import { v4 as uuidv4 } from "uuid";
import '../styles/CanvasEditor.css';

// YouTube thumbnail dimensions
const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;

const downloadURI = (uri, name) => {
  const link = document.createElement("a");
  link.download = name;
  link.href = uri || "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CanvasEditor = ({ baseThumbnail, faceCutouts = [] }) => {
  const [faceCutoutImages, setFaceCutoutImages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [baseImage, setBaseImage] = useState(null);

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const fileRef = useRef(null);

  // Load base thumbnail image
  React.useEffect(() => {
    if (baseThumbnail?.url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setBaseImage(img);
      };
      img.src = baseThumbnail.url;
    }
  }, [baseThumbnail]);

  const onImportImageSelect = useCallback(
    (e) => {
      if (e.target.files?.[0]) {
        const imageUrl = URL.createObjectURL(e.target.files[0]);
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          const id = uuidv4();
          const newFaceCutout = {
            id,
            image,
            x: 100,
            y: 100,
            width: 200,
            height: 200,
            name: `face-${Date.now()}`
          };
          setFaceCutoutImages(prev => [...prev, newFaceCutout]);
        };
        image.src = imageUrl;
      }
      e.target.files = null;
    },
    []
  );

  const onImportImageClick = useCallback(() => {
    fileRef?.current && fileRef?.current?.click();
  }, []);

  const onExportClick = useCallback(() => {
    const dataUri = stageRef?.current?.toDataURL({ 
      pixelRatio: 2,
      mimeType: 'image/png',
      quality: 1 
    });
    downloadURI(dataUri, "podcast-thumbnail.png");
  }, []);

  const onClear = useCallback(() => {
    setFaceCutoutImages([]);
    setSelectedId(null);
  }, []);

  const onShapeClick = useCallback(
    (e, imageId) => {
      if (e.target === e.target.getStage()) return;
      
      e.cancelBubble = true;
      setSelectedId(imageId);
      
      const currentTarget = e.currentTarget;
      transformerRef?.current?.nodes([currentTarget]);
      transformerRef?.current?.getLayer()?.batchDraw();
    },
    []
  );

  const onBgClick = useCallback(
    (e) => {
      // Only deselect if clicking on stage background or base image
      if (e.target === e.target.getStage() || e.target.name() === 'base-thumbnail') {
        setSelectedId(null);
        transformerRef?.current?.nodes([]);
        transformerRef?.current?.getLayer()?.batchDraw();
      }
    },
    []
  );

  const updateFaceCutout = useCallback((id, newAttrs) => {
    setFaceCutoutImages(prev => 
      prev.map(img => img.id === id ? { ...img, ...newAttrs } : img)
    );
  }, []);

  const onDragEnd = useCallback((e, imageId) => {
    updateFaceCutout(imageId, {
      x: e.target.x(),
      y: e.target.y()
    });
  }, [updateFaceCutout]);

  const onTransformEnd = useCallback((e, imageId) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    // Reset scale to 1 to avoid compound scaling
    node.scaleX(1);
    node.scaleY(1);
    
    updateFaceCutout(imageId, {
      x: node.x(),
      y: node.y(),
      width: Math.max(20, node.width() * scaleX),
      height: Math.max(20, node.height() * scaleY),
    });
  }, [updateFaceCutout]);

  const addFaceCutoutFromList = useCallback((face) => {
    if (!face.cutoutUrl) {
      alert('Face cutout URL is missing');
      return;
    }
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const id = uuidv4();
      const newFaceCutout = {
        id,
        image: img,
        x: Math.random() * (CANVAS_WIDTH - 200),
        y: Math.random() * (CANVAS_HEIGHT - 200),
        width: 200,
        height: 200,
        name: face.name
      };
      setFaceCutoutImages(prev => [...prev, newFaceCutout]);
    };
    img.src = face.cutoutUrl;
  }, []);

  const deleteSelected = useCallback(() => {
    if (selectedId) {
      setFaceCutoutImages(prev => prev.filter(img => img.id !== selectedId));
      setSelectedId(null);
      transformerRef?.current?.nodes([]);
    }
  }, [selectedId]);

  React.useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;
  
    const transformer = transformerRef.current;
  
    if (selectedId) {
      const selectedNode = stageRef.current.findOne(`#${selectedId}`);
      if (selectedNode) {
        transformer.nodes([selectedNode]);
      } else {
        transformer.nodes([]);
      }
    } else {
      transformer.nodes([]);
    }
    transformer.getLayer().batchDraw();
  }, [selectedId]);

  if (!baseThumbnail) {
    return (
      <div className="canvas-editor component-card disabled">
        <h2>Step 4: Create Final Thumbnail</h2>
        <p className="disabled-message">
          Please generate a base thumbnail first to proceed with canvas editing.
        </p>
      </div>
    );
  }

  return (
    <div className="canvas-editor component-card">
      <h2>Step 4: Create Final Thumbnail</h2>
      
      <div className="editor-container">
        <div className="canvas-section">
          {/* Canvas Controls */}
          <div className="canvas-controls" style={{ marginBottom: '16px' }}>
            <div className="control-group">
              <input
                type="file"
                ref={fileRef}
                onChange={onImportImageSelect}
                style={{ display: "none" }}
                accept="image/*"
              />
              <button 
                onClick={onImportImageClick}
                className="control-btn"
                style={{ backgroundColor: '#4299e1' }}
              >
                📤 Import Face Cutout
              </button>
              
              <button 
                onClick={deleteSelected}
                disabled={!selectedId}
                className="control-btn delete-btn"
              >
                🗑️ Delete Selected
              </button>
              
              <button 
                onClick={onClear}
                className="control-btn clear-btn"
              >
                🧹 Clear All Faces
              </button>
              
              <button 
                onClick={onExportClick}
                className="download-btn"
                style={{ backgroundColor: '#48bb78' }}
              >
                💾 Export Thumbnail
              </button>
            </div>
          </div>

          {/* Canvas */}
          <div className="canvas-container">
            <Stage
              height={CANVAS_HEIGHT}
              width={CANVAS_WIDTH}
              ref={stageRef}
              onClick={onBgClick}
              style={{
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                maxWidth: '100%',
                height: 'auto'
              }}
            >
              <Layer>
                {/* Background */}
                <KonvaRect
                  x={0}
                  y={0}
                  height={CANVAS_HEIGHT}
                  width={CANVAS_WIDTH}
                  fill="white"
                  name="bg"
                />
                
                {/* Base Thumbnail - Always displayed and not selectable */}
                {baseImage && (
                  <KonvaImage
                    image={baseImage}
                    x={0}
                    y={0}
                    width={CANVAS_WIDTH}
                    height={CANVAS_HEIGHT}
                    name="base-thumbnail"
                    listening={true} // Make it non-interactive
                  />
                )}
                
                {/* Face Cutouts */}
                {faceCutoutImages.map((faceCutout) => (
                  <KonvaImage
                    id={faceCutout.id}
                    key={faceCutout.id}
                    image={faceCutout.image}
                    x={faceCutout.x}
                    y={faceCutout.y}
                    width={faceCutout.width}
                    height={faceCutout.height}
                    draggable={true}
                    onClick={(e) => onShapeClick(e, faceCutout.id)}
                    onTap={(e) => onShapeClick(e, faceCutout.id)}
                    onDragEnd={(e) => onDragEnd(e, faceCutout.id)}
                    onTransformEnd={(e) => onTransformEnd(e, faceCutout.id)}
                    stroke={selectedId === faceCutout.id ? "#4299e1" : undefined}
                    strokeWidth={selectedId === faceCutout.id ? 2 : 0}
                  />
                ))}
                
                {/* Transformer */}
                <Transformer
                  ref={transformerRef}
                  flipEnabled={false}
                  rotateEnabled={true}
                  borderEnabled={true}
                  borderStroke="#4299e1"
                  borderStrokeWidth={2}
                  borderDash={[4, 4]}
                  anchorFill="#4299e1"
                  anchorStroke="#2d3748"
                  anchorSize={8}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (Math.abs(newBox.width) < 20 || Math.abs(newBox.height) < 20) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
          </div>
        </div>
        
        {/* Faces Panel */}
        <div className="faces-panel">
          <h3>Available Face Cutouts</h3>
          {faceCutouts.length === 0 ? (
            <p className="no-faces">
              No face cutouts available. Upload and process guest faces in Step 3.
            </p>
          ) : (
            <div className="faces-list">
              {faceCutouts.map((face) => (
                <div key={face.id} className="face-item">
                  <div className="face-preview">
                    <img src={face.cutoutUrl} alt={face.name} />
                  </div>
                  <div className="face-info">
                    <p className="face-name">{face.name}</p>
                    <button 
                      onClick={() => addFaceCutoutFromList(face)}
                      className="add-face-btn"
                    >
                      Add to Canvas
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="help-text">
        <p>🎨 Click on face cutouts to select them, then drag to move and drag corners to resize/rotate</p>
        <p>📤 Import face cutouts manually or add them from the processed faces list</p>
        <p>💾 Export your final thumbnail when you're satisfied with the composition</p>
      </div>
    </div>
  );
};

export default CanvasEditor;
